import { useState, useEffect, useRef, useCallback } from 'react';
import { FaPaperPlane, FaPlus, FaComments, FaTrash, FaTrashAlt, FaChevronDown, FaArrowLeft, FaRegSmile, FaTimes, FaSync, FaPhone, FaCheck, FaCheckDouble } from 'react-icons/fa';
import api from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { getSocket, socketSupported } from '../../socket';
import { Avatar } from '../../components/Avatar';
import { useCall } from '../../contexts/CallContext';

// How often to refresh when no live socket connection exists (Cloudflare
// Worker / "Cloud" mode has no persistent-connection support, so this is
// the only way new messages show up without a manual refresh there).
const POLL_INTERVAL_MS = 3000;

const EMOJIS = [
  '😀', '😂', '😅', '😊', '🙂', '😉', '😍', '😘', '😜', '🤔', '😎', '😴', '😭', '😡', '🥳', '🤗',
  '🙄', '😢', '😱', '🤯', '🥰', '😇', '🤩', '🙃',
  '👍', '👎', '👏', '🙌', '🙏', '💪', '🤝', '👌', '✌️', '🤞', '👋', '🫡',
  '❤️', '💙', '💛', '💚', '💜', '🧡', '🖤', '💔',
  '🔥', '✨', '🎉', '🎂', '🚀', '⭐', '💡', '✅', '❌', '⚠️',
];

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function PresenceDot({ online, size = 10 }) {
  return (
    <span
      className={`inline-block rounded-full flex-shrink-0 ${online ? 'bg-emerald-400' : 'bg-gray-300'}`}
      style={{
        width: size, height: size,
        boxShadow: online ? '0 0 0 2px rgba(52,211,153,0.25), 0 0 8px 2px rgba(52,211,153,0.75)' : undefined,
      }}
      title={online ? 'Online' : 'Offline'}
    />
  );
}

export default function Chat() {
  const { user } = useAuth();
  const { startCall } = useCall();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId]     = useState(null);
  const [messages, setMessages]     = useState([]);
  const [draft, setDraft]           = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [employees, setEmployees]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [sending, setSending]       = useState(false);
  const [error, setError]           = useState('');
  const [onlineIds, setOnlineIds]   = useState(() => new Set());
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  // Fallback header info for the open thread when its conversation isn't
  // (yet, or currently) in the filtered `conversations` list -- e.g. one
  // you just cleared-and-reopened has nothing visible since the clear, so
  // it's correctly absent from the sidebar, but the thread you just opened
  // must still render rather than silently showing nothing.
  const [activeFallback, setActiveFallback] = useState(null);
  const bottomRef = useRef(null);
  const activeIdRef = useRef(null);
  activeIdRef.current = activeId;
  const newChatRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const draftInputRef = useRef(null);

  const loadConversations = useCallback(() => {
    return api.getConversations().then((rows) => {
      setConversations(rows);
      // No live socket on this backend (Cloudflare Worker "Cloud" mode) --
      // derive presence from the heartbeat-based `other_user_online` flag
      // each poll returns instead. Local mode keeps its own socket-driven
      // onlineIds untouched (skip merging so a stale poll can't clobber it).
      if (!socketSupported()) {
        setOnlineIds((prev) => {
          const next = new Set(prev);
          rows.forEach((c) => {
            if (c.other_user_online) next.add(c.other_user_id); else next.delete(c.other_user_id);
          });
          return next;
        });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadConversations().finally(() => setLoading(false));
  }, [loadConversations]);

  // Manual + auto-on-resume refresh -- Cloud mode only. That backend has no
  // socket, so it depends on the 6s poll below, which mobile PWAs suspend
  // whenever the app is backgrounded or the screen locks (regular JS timer
  // throttling, not a caching bug) -- this is what catches it back up.
  const refreshNow = useCallback(() => {
    loadConversations();
    if (activeIdRef.current) {
      api.getMessages(activeIdRef.current)
        .then((fresh) => setMessages((prev) => (prev.length === fresh.length ? prev : fresh)))
        .catch(() => {});
    }
  }, [loadConversations]);

  useEffect(() => {
    if (socketSupported()) return;
    const onResume = () => { if (document.visibilityState === 'visible') refreshNow(); };
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    window.addEventListener('pageshow', onResume);
    return () => {
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('pageshow', onResume);
    };
  }, [refreshNow]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const onNewMessage = ({ conversationId, message }) => {
      if (conversationId === activeIdRef.current) {
        setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
        if (message.sender_id !== user.id) {
          api.markConversationRead(conversationId).catch(() => {});
        }
      }
      loadConversations();
    };
    const onRead = ({ conversationId }) => {
      if (conversationId === activeIdRef.current) {
        const now = new Date().toISOString();
        setMessages((prev) => prev.map((m) => (m.sender_id === user.id && !m.read_at ? { ...m, read_at: now } : m)));
      }
      loadConversations();
    };
    const onDeleted = ({ conversationId, messageId }) => {
      if (conversationId === activeIdRef.current) {
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
      }
    };
    const onCleared = ({ conversationId }) => {
      if (conversationId === activeIdRef.current) setMessages([]);
      loadConversations();
    };

    const onSnapshot = ({ onlineUserIds }) => setOnlineIds(new Set(onlineUserIds));
    const onOnline = ({ userId }) => setOnlineIds((prev) => new Set(prev).add(userId));
    const onOffline = ({ userId }) => setOnlineIds((prev) => {
      const next = new Set(prev);
      next.delete(userId);
      return next;
    });

    socket.on('message:new', onNewMessage);
    socket.on('message:read', onRead);
    socket.on('message:deleted', onDeleted);
    socket.on('conversation:cleared', onCleared);
    socket.on('presence:snapshot', onSnapshot);
    socket.on('presence:online', onOnline);
    socket.on('presence:offline', onOffline);
    return () => {
      socket.off('message:new', onNewMessage);
      socket.off('message:read', onRead);
      socket.off('message:deleted', onDeleted);
      socket.off('conversation:cleared', onCleared);
      socket.off('presence:snapshot', onSnapshot);
      socket.off('presence:online', onOnline);
      socket.off('presence:offline', onOffline);
    };
  }, [loadConversations, user.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Polling fallback for backends with no live socket (Cloudflare Worker).
  // Harmless when a socket IS connected too -- just a redundant refresh.
  useEffect(() => {
    if (socketSupported()) return;
    const interval = setInterval(() => {
      loadConversations();
      if (activeIdRef.current) {
        api.getMessages(activeIdRef.current)
          .then((fresh) => setMessages((prev) => (prev.length === fresh.length ? prev : fresh)))
          .catch(() => {});
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadConversations]);

  // Close the "New Message" dropdown on outside click
  useEffect(() => {
    if (!showNewChat) return;
    const handleClickOutside = (e) => {
      if (newChatRef.current && !newChatRef.current.contains(e.target)) setShowNewChat(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNewChat]);

  // Close the emoji picker on outside click
  useEffect(() => {
    if (!showEmojiPicker) return;
    const handleClickOutside = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) setShowEmojiPicker(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  const openConversation = (conversationId, fallback = null) => {
    setActiveId(conversationId);
    if (fallback) setActiveFallback(fallback);
    setError('');
    api.getMessages(conversationId).then(setMessages).catch(() => setError('Could not load messages.'));
    api.markConversationRead(conversationId).then(loadConversations).catch(() => {});
  };

  const toggleNewChat = () => {
    if (!showNewChat) {
      api.getChatEmployees().then((rows) => {
        setEmployees(rows);
        if (!socketSupported()) {
          setOnlineIds((prev) => {
            const next = new Set(prev);
            rows.forEach((e) => { if (e.online) next.add(e.id); else next.delete(e.id); });
            return next;
          });
        }
      }).catch(() => {});
    }
    setShowNewChat((v) => !v);
  };

  const startChat = async (employeeId) => {
    try {
      const conv = await api.startConversation(employeeId);
      setShowNewChat(false);
      await loadConversations();
      openConversation(conv.id, {
        id: conv.id,
        other_user_id: conv.otherUserId,
        other_user_name: conv.otherUserName,
        other_user_title: null,
      });
    } catch (err) {
      setError(err.message || 'Could not start conversation.');
    }
  };

  const insertEmoji = (emoji) => {
    const el = draftInputRef.current;
    const start = el?.selectionStart ?? draft.length;
    const end = el?.selectionEnd ?? draft.length;
    setDraft(draft.slice(0, start) + emoji + draft.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      const pos = start + emoji.length;
      el?.setSelectionRange?.(pos, pos);
    });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !activeId) return;
    setSending(true);
    setError('');
    try {
      const message = await api.sendMessage(activeId, body);
      setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
      setDraft('');
      loadConversations();
    } catch (err) {
      setError(err.message || 'Could not send message.');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!activeId) return;
    setMessages((prev) => prev.filter((m) => m.id !== messageId));
    try {
      await api.deleteMessage(activeId, messageId);
    } catch (err) {
      setError(err.message || 'Could not delete message.');
      openConversation(activeId);
    }
  };

  const handleClearHistory = async () => {
    if (!activeId) return;
    setMessages([]);
    try {
      await api.clearConversation(activeId);
      loadConversations();
    } catch (err) {
      setError(err.message || 'Could not clear history.');
      openConversation(activeId);
    }
  };

  // Removes a conversation from the list entirely (same underlying "clear"
  // action as handleClearHistory) -- it reappears on its own the moment the
  // other person sends a new message, since that's the whole point: this is
  // a per-viewer declutter, not blocking or deleting anything for real.
  const handleRemoveConversation = async (e, conversationId) => {
    e.stopPropagation();
    setConversations((prev) => prev.filter((c) => c.id !== conversationId));
    if (activeId === conversationId) {
      setActiveId(null);
      setMessages([]);
    }
    try {
      await api.clearConversation(conversationId);
    } catch (err) {
      setError(err.message || 'Could not remove conversation.');
      loadConversations();
    }
  };

  const active = conversations.find((c) => c.id === activeId)
    || (activeFallback?.id === activeId ? activeFallback : null);
  // Always shown now: Local mode gets instant socket-driven presence,
  // Cloud mode (the Worker) gets it from the heartbeat/poll merge above --
  // slightly laggier (~poll-interval), but no longer permanently hidden.
  const showPresence = true;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0c1b4d] flex items-center gap-2">
          <FaComments className="text-[#D4AF37]" /> Messages
        </h1>

        {/* New Message dropdown */}
        <div className="relative" ref={newChatRef}>
          <button
            onClick={toggleNewChat}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#0c1b4d] to-blue-900 text-white rounded-lg text-sm font-semibold hover:from-blue-900 hover:to-[#0c1b4d] transition-all shadow-sm"
          >
            <FaPlus size={12} /> New Message
            <FaChevronDown size={10} className={`transition-transform ${showNewChat ? 'rotate-180' : ''}`} />
          </button>
          {showNewChat && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 py-1.5 z-50 max-h-96 overflow-y-auto">
              <p className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Start a conversation</p>
              {employees.length === 0 ? (
                <p className="px-4 py-3 text-sm text-gray-400">No other chat-enabled employees found.</p>
              ) : (
                employees.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => startChat(e.id)}
                    className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition-colors flex items-center gap-3"
                  >
                    <div className="relative flex-shrink-0">
                      <Avatar id={e.id} name={e.name} size={32} />
                      {showPresence && (
                        <span className="absolute -bottom-0.5 -right-0.5">
                          <PresenceDot online={onlineIds.has(e.id)} size={9} />
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {e.name}{' '}
                        {e.employee_id && <span className="text-gray-400 font-normal">({e.employee_id})</span>}
                      </p>
                      {e.title && <p className="text-xs text-gray-500 truncate">{e.title}</p>}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-2.5 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-xl border-2 border-[#0c1b4d]/15 overflow-hidden flex" style={{ height: '68vh' }}>
        {/* Conversation list */}
        <div className={`${activeId ? 'hidden sm:block' : 'block'} w-full sm:w-80 border-r border-gray-100 overflow-y-auto flex-shrink-0 bg-gradient-to-b from-gray-50 to-white`}>
          {loading ? (
            <p className="p-4 text-sm text-gray-400">Loading conversations…</p>
          ) : conversations.length === 0 ? (
            <p className="p-4 text-sm text-gray-400">No conversations yet. Start one with the button above.</p>
          ) : (
            conversations.map((c) => (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                onClick={() => openConversation(c.id, c)}
                onKeyDown={(e) => { if (e.key === 'Enter') openConversation(c.id, c); }}
                className={`group w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-blue-50/70 transition-colors flex items-center gap-3 cursor-pointer ${
                  activeId === c.id ? 'bg-blue-50 border-l-4 border-l-[#0c1b4d]' : 'border-l-4 border-l-transparent'
                }`}
              >
                <div className="relative flex-shrink-0">
                  <Avatar id={c.other_user_id} name={c.other_user_name} />
                  {showPresence && (
                    <span className="absolute -bottom-0.5 -right-0.5">
                      <PresenceDot online={onlineIds.has(c.other_user_id)} />
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm text-gray-800 truncate">{c.other_user_name}</span>
                    {c.unread_count > 0 && (
                      <span className="flex-shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-br from-[#D4AF37] to-amber-500 text-[#0c1b4d] text-[10px] font-bold flex items-center justify-center shadow-sm">
                        {c.unread_count}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {c.last_message_body ? c.last_message_body : 'No messages yet'}
                  </p>
                </div>
                <button
                  onClick={(e) => handleRemoveConversation(e, c.id)}
                  title="Remove from list"
                  className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-gray-400 hover:text-red-500 hover:bg-white rounded-full"
                >
                  <FaTimes size={12} />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Thread */}
        <div
          className={`${activeId ? 'flex' : 'hidden'} sm:flex flex-col flex-1 min-w-0`}
          style={{ background: 'linear-gradient(180deg, #f7f9fc 0%, #eef2f9 100%)' }}
        >
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 text-sm gap-2">
              <FaComments size={32} className="text-gray-200" />
              Select a conversation to start chatting
            </div>
          ) : (
            <>
              <div className="px-3 sm:px-5 py-3 border-b border-gray-100 bg-white/80 backdrop-blur flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => setActiveId(null)}
                  className="sm:hidden p-2 -ml-1 text-gray-500 hover:text-[#0c1b4d] hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                  title="Back to conversations"
                >
                  <FaArrowLeft size={16} />
                </button>
                <Avatar id={active.other_user_id} name={active.other_user_name} size={32} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-800 truncate">{active.other_user_name}</p>
                  {showPresence && (
                    <p className="text-xs text-gray-400 flex items-center gap-1.5">
                      <PresenceDot online={onlineIds.has(active.other_user_id)} size={7} />
                      {onlineIds.has(active.other_user_id) ? 'Online' : 'Offline'}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => startCall(active.other_user_id, active.other_user_name)}
                  disabled={!socketSupported() || !onlineIds.has(active.other_user_id)}
                  title={
                    !socketSupported()
                      ? 'Calls require Oracle Cloud mode'
                      : onlineIds.has(active.other_user_id)
                        ? 'Call'
                        : `${active.other_user_name} is offline`
                  }
                  className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400 disabled:cursor-not-allowed"
                >
                  <FaPhone size={14} />
                </button>
                {!socketSupported() && (
                  <button
                    onClick={refreshNow}
                    title="Refresh"
                    className="p-2 text-gray-400 hover:text-[#0c1b4d] hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <FaSync size={13} />
                  </button>
                )}
                <button
                  onClick={handleClearHistory}
                  title="Clear conversation for you"
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <FaTrashAlt size={14} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`group flex items-end gap-1.5 ${mine ? 'justify-end' : 'justify-start'}`}>
                      {mine && (
                        <button
                          onClick={() => handleDeleteMessage(m.id)}
                          title="Delete for you"
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-gray-400 hover:text-red-500 rounded-full hover:bg-white"
                        >
                          <FaTrash size={11} />
                        </button>
                      )}
                      <div
                        className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm shadow-sm ${
                          mine
                            ? 'bg-gradient-to-br from-[#0c1b4d] to-blue-800 text-white rounded-br-md'
                            : 'bg-white text-[#0c1b4d] border border-gray-100 rounded-bl-md'
                        }`}
                      >
                        <p className="whitespace-pre-wrap break-words">{m.body}</p>
                        <p className={`text-[10px] mt-1 flex items-center gap-1 ${mine ? 'text-blue-200 justify-end' : 'text-gray-400'}`}>
                          {formatTime(m.created_at)}
                          {mine && (
                            m.read_at
                              ? <FaCheckDouble size={10} className="text-sky-300" title="Read" />
                              : <FaCheck size={10} title="Sent" />
                          )}
                        </p>
                      </div>
                      {!mine && (
                        <button
                          onClick={() => handleDeleteMessage(m.id)}
                          title="Delete for you"
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-gray-400 hover:text-red-500 rounded-full hover:bg-white"
                        >
                          <FaTrash size={11} />
                        </button>
                      )}
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={handleSend} className="p-3 border-t border-gray-100 bg-white/80 backdrop-blur flex items-center gap-2">
                <div className="relative flex-shrink-0" ref={emojiPickerRef}>
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker((v) => !v)}
                    title="Emoji"
                    className={`p-2.5 rounded-full transition-colors ${showEmojiPicker ? 'bg-blue-100 text-[#0c1b4d]' : 'text-gray-400 hover:text-[#0c1b4d] hover:bg-gray-100'}`}
                  >
                    <FaRegSmile size={18} />
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute bottom-full left-0 mb-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 p-2 z-50">
                      <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto">
                        {EMOJIS.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => insertEmoji(emoji)}
                            className="text-lg leading-none p-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <input
                  ref={draftInputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type a message…"
                  className="flex-1 px-4 py-2.5 border-2 border-[#0c1b4d]/30 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-[#0c1b4d] bg-white"
                />
                <button
                  type="submit"
                  disabled={sending || !draft.trim()}
                  className="p-3 bg-gradient-to-br from-[#0c1b4d] to-blue-800 text-white rounded-full hover:from-blue-900 hover:to-[#0c1b4d] transition-all disabled:opacity-40 shadow-sm"
                >
                  <FaPaperPlane size={14} />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
